/**
 * WhatifPanel — Parametre simülasyonu (Karar Destek Paneli).
 *
 * Patron sliderı oynar → /api/reports/whatif çağırır → Mevcut vs Yeni P&L karşılaştırması.
 * Saf hesap, AI yok.
 *
 * Props:
 *   range: { start, end }
 *   firma: string ('' | TORUNA_KASAP | INGA_BURGER | ALTI_USTU_KOFTE)
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../styles/ThemeProvider';
import { formatTRY } from '../../utils/analytics/format';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const SLIDER_DEFS = [
  { key: 'kredi_karti_komisyon_pct', label: 'Kredi Kartı Komisyonu', min: 0, max: 5, step: 0.1, unit: '%' },
  { key: 'yemek_karti_komisyon_pct', label: 'Yemek Kartı Komisyonu', min: 0, max: 20, step: 0.5, unit: '%' },
  { key: 'online_komisyon_pct', label: 'Online Platform Komisyonu', min: 0, max: 50, step: 0.5, unit: '%' },
  { key: 'fiyat_pct_zam', label: 'Menü Fiyat Zammı', min: -10, max: 30, step: 0.5, unit: '%' },
  { key: 'personel_pct_degisim', label: 'Personel Gideri Değişimi', min: -30, max: 50, step: 1, unit: '%' },
];

export default function WhatifPanel({ range, firma }) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const body = {
          baslangic: range.start,
          bitis: range.end,
          firma: firma || null,
          ...params,
        };
        const res = await fetch(`${API}/api/reports/whatif`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
        if (res.ok) setData(await res.json());
      } catch (err) {
        // sessiz
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [open, params, range.start, range.end, firma]);

  const setParam = (key, val) => setParams((p) => ({ ...p, [key]: val }));
  const reset = () => { setParams({}); setData(null); };

  const Row = ({ label, mevcut, yeni, fark }) => {
    const farkColor = fark > 0 ? theme.color.success : (fark < 0 ? theme.color.danger : theme.color.textSoft);
    return (
      <tr style={{ borderBottom: `1px solid ${theme.color.border}` }}>
        <td style={{ padding: '6px 12px' }}>{label}</td>
        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTRY(mevcut, { decimals: 0 })}</td>
        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatTRY(yeni, { decimals: 0 })}</td>
        <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', color: farkColor }}>
          {fark > 0 ? '+' : ''}{formatTRY(fark, { decimals: 0 })}
        </td>
      </tr>
    );
  };

  return (
    <section style={{ background: theme.color.surface, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}` }}>
      <header
        onClick={() => setOpen(!open)}
        style={{
          padding: theme.space.md,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0, color: theme.color.text }}>What-if Simülasyonu</h3>
        <span style={{ color: theme.color.textSoft, fontSize: 12 }}>
          {open ? '▼ Kapat' : '▶ Aç (parametre simülasyonu)'}
        </span>
      </header>

      {open && (
        <div style={{ padding: theme.space.md, borderTop: `1px solid ${theme.color.border}` }}>
          <div style={{ fontSize: 12, color: theme.color.textSoft, marginBottom: theme.space.md }}>
            Sliderları oynat → P&L anlık yeniden hesaplanır. Saf matematik, kayıt yapılmaz.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: theme.space.md, marginBottom: theme.space.lg }}>
            {SLIDER_DEFS.map((def) => {
              const val = params[def.key];
              const placeholder = def.key.startsWith('fiyat') || def.key.startsWith('personel') ? 0 : '—';
              return (
                <div key={def.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span>{def.label}</span>
                    <span style={{ fontFamily: 'monospace', color: val !== undefined ? theme.color.text : theme.color.textSoft }}>
                      {val !== undefined ? `${val}${def.unit}` : `(mevcut)`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={def.min}
                    max={def.max}
                    step={def.step}
                    value={val !== undefined ? val : placeholder === '—' ? def.min : 0}
                    onChange={(e) => setParam(def.key, parseFloat(e.target.value))}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: theme.color.textSoft }}>
                    <span>{def.min}{def.unit}</span>
                    <span>{def.max}{def.unit}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={reset}
            style={{
              padding: '6px 14px',
              border: `1px solid ${theme.color.border}`,
              background: theme.color.surface,
              color: theme.color.text,
              borderRadius: theme.radius.sm,
              cursor: 'pointer',
              fontSize: 12,
              marginBottom: theme.space.md,
            }}
          >
            Sıfırla
          </button>

          {loading && <div style={{ color: theme.color.textSoft, fontSize: 12 }}>Hesaplanıyor...</div>}

          {data && (
            <div style={{ marginTop: theme.space.md, overflow: 'auto' }}>
              <h4 style={{ marginTop: 0, color: theme.color.text }}>Karşılaştırma</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${theme.color.border}`, textAlign: 'right' }}>
                    <th style={{ padding: '6px 12px', textAlign: 'left' }}>Kalem</th>
                    <th style={{ padding: '6px 12px' }}>Mevcut</th>
                    <th style={{ padding: '6px 12px' }}>Yeni</th>
                    <th style={{ padding: '6px 12px' }}>Fark</th>
                  </tr>
                </thead>
                <tbody>
                  <Row label="Net Ciro Brut"  mevcut={data.mevcut.net_ciro_brut} yeni={data.yeni.net_ciro_brut} fark={data.fark.net_ciro_brut} />
                  <Row label="Komisyon"        mevcut={(data.mevcut.komisyon || {}).TOPLAM || 0} yeni={(data.yeni.komisyon || {}).TOPLAM || 0} fark={((data.yeni.komisyon || {}).TOPLAM || 0) - ((data.mevcut.komisyon || {}).TOPLAM || 0)} />
                  <Row label="Net Ciro"        mevcut={data.mevcut.net_ciro} yeni={data.yeni.net_ciro} fark={data.fark.net_ciro} />
                  <Row label="SMM"             mevcut={data.mevcut.smm} yeni={data.yeni.smm} fark={data.fark.smm} />
                  <Row label="Brüt Kâr"        mevcut={data.mevcut.brut_kar} yeni={data.yeni.brut_kar} fark={data.fark.brut_kar} />
                  <Row label="OPEX Toplam"     mevcut={data.mevcut.opex_toplam} yeni={data.yeni.opex_toplam} fark={data.fark.opex_toplam} />
                  <tr style={{ background: theme.color.surfaceAlt, borderTop: `2px solid ${theme.color.border}`, fontWeight: 700 }}>
                    <td style={{ padding: '6px 12px' }}>EBITDA</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTRY(data.mevcut.ebitda, { decimals: 0 })}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTRY(data.yeni.ebitda, { decimals: 0 })}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', color: data.fark.ebitda >= 0 ? theme.color.success : theme.color.danger }}>
                      {data.fark.ebitda > 0 ? '+' : ''}{formatTRY(data.fark.ebitda, { decimals: 0 })}
                    </td>
                  </tr>
                  <Row label="Net Kâr"         mevcut={data.mevcut.net_kar} yeni={data.yeni.net_kar} fark={data.fark.net_kar} />
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
